import { httpsCallable, HttpsCallableResult } from 'firebase/functions';
import { functions } from '../config/firebase';
import { leadGenerationStatusService } from './leadGenerationStatusService';

// Types for the cloud function request and response
export interface GenerateLeadRequest {
  companyName: string;
  geography: string;
  statusId: string;
}

export interface GenerateLeadResponse {
  success: boolean;
  message: string;
  leadId?: string;
  statusId?: string;
  data?: any;
  error?: string;
  error_code?: string;
}

// Error codes from backend
export enum LeadGenerationErrorCode {
  INVALID_INPUT = 'invalid_input',
  PROCESSING_ERROR = 'processing_error',
  AUTHENTICATION_ERROR = 'authentication_error',
  EXTERNAL_API_ERROR = 'external_api_error',
}

// Configuration for the service
const SERVICE_CONFIG = {
  TIMEOUT_MS: 600000, // 10 minutes - increased to match cloud function timeout + buffer
  MAX_RETRIES: 0, // NO RETRIES - function handles failures internally
  RETRY_DELAY_MS: 1000, // Not used anymore
} as const;

/**
 * Service to handle lead generation via Firebase Cloud Functions
 */
export class LeadGenerationService {
  private generateLeadFunction = httpsCallable<GenerateLeadRequest, GenerateLeadResponse>(
    functions,
    'generate_lead'
  );

  constructor() {
    // Set timeout for the function
    this.generateLeadFunction = httpsCallable<GenerateLeadRequest, GenerateLeadResponse>(
      functions,
      'generate_lead',
      {
        timeout: SERVICE_CONFIG.TIMEOUT_MS,
      }
    );
  }

  /**
   * Generate a new lead using the Python cloud function with retry logic
   * @param userId - User ID (required for status tracking)
   * @param companyName - Name of the company
   * @param geography - Geographic location
   * @returns Promise with the result
   */
  async generateLead(userId: string, companyName: string, geography: string): Promise<GenerateLeadResponse> {
    let statusId: string | null = null;

    try {
      // Step 1: Create status record first
      console.log('Creating status record for lead generation...');
      statusId = await leadGenerationStatusService.createStatusRecord(userId, companyName, geography);

      // Step 2: Prepare function call with status ID
      const sanitizedInput = this.sanitizeInput({ companyName, geography, statusId });

      // Step 3: Call the function ONCE - no retries to prevent multiple invocations
      console.log(`🚀 Calling generate_lead function with statusId: ${statusId}`);
      console.log(`⚠️  NO RETRIES - function handles failures internally to prevent duplicates`);

      try {
        const result: HttpsCallableResult<GenerateLeadResponse> = await this.generateLeadFunction(sanitizedInput);
        console.log(`✅ Function call completed successfully for statusId: ${statusId}`);
        return result.data;
      } catch (error: any) {
        console.error(`❌ Function call failed for statusId: ${statusId}:`, error);
        throw this.transformError(error);
      }

    } catch (error: any) {
      // IMPORTANT: Don't mark as failed from frontend - let the backend handle all status updates
      // The backend will update the status appropriately based on the actual processing result
      console.log(`⚠️  Frontend error for statusId: ${statusId}, but letting backend handle status updates:`, error.message);

      // Re-throw the original error
      throw error;
    }
  }

  /**
   * Sanitize input data
   * @param input - Input data to sanitize
   * @returns Sanitized input
   */
  private sanitizeInput(input: GenerateLeadRequest): GenerateLeadRequest {
    return {
      companyName: input.companyName.trim(),
      geography: input.geography.trim(),
      statusId: input.statusId.trim(),
    };
  }

  /**
   * Check if error should not be retried
   * @param error - Error to check
   * @returns True if error should not be retried
   */
  private isNonRetryableError(error: any): boolean {
    const nonRetryableCodes = [
      'unauthenticated',
      'invalid-argument',
      'permission-denied',
    ];

    return nonRetryableCodes.includes(error.code);
  }

  /**
   * Transform Firebase error to user-friendly error
   * @param error - Original error
   * @returns User-friendly error
   */
  private transformError(error: any): Error {
    // Handle different types of errors
    switch (error.code) {
      case 'unauthenticated':
        return new Error('You must be logged in to generate leads');

      case 'invalid-argument':
        return new Error(error.message || 'Invalid input provided. Please check your company name and geography.');

      case 'permission-denied':
        return new Error('You do not have permission to generate leads');

      case 'already-exists':
        return new Error('Lead generation is already in progress for this request. Please wait.');

      case 'not-found':
        return new Error('Request not found. Please try submitting again.');

      case 'deadline-exceeded':
      case 'timeout':
        return new Error('Request timed out. Please try again.');

      case 'resource-exhausted':
        return new Error('Service is currently at capacity. Please try again later.');

      case 'internal':
      case 'unknown':
        return new Error('Server error occurred. Please try again later.');

      case 'unavailable':
        return new Error('Service is temporarily unavailable. Please try again.');

      default:
        return new Error(error.message || 'Failed to generate lead. Please try again.');
    }
  }

  /**
   * Delay function for retry logic
   * @param ms - Milliseconds to delay
   * @returns Promise that resolves after delay
   */
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Get lead generation status by status ID
   * @param statusId - Status document ID
   * @returns Promise with status data
   */
  async getLeadStatus(statusId: string): Promise<any> {
    // This would typically call another Cloud Function or Firestore directly
    // For now, this is a placeholder for future implementation
    console.log('Getting lead status for:', statusId);
    return Promise.resolve({ status: 'unknown' });
  }
}

// Export a singleton instance
export const leadGenerationService = new LeadGenerationService();